import * as React from "react";
import {
    makeStyles,
    shorthands,
    tokens,
    Tooltip,
    AppItem,
    Hamburger,
    NavDrawer,
    NavDrawerBody,
    NavDrawerHeader,
    NavItem
} from "@fluentui/react-components";
import {
    Cookies20Filled,
    Cookies20Regular,
    InprivateAccount20Filled,
    InprivateAccount20Regular,
    DocumentEdit20Filled,
    DocumentEdit20Regular,
    BookContacts20Filled,
    BookContacts20Regular,
    PersonCircle32Regular,
    bundleIcon,
    ArrowCounterclockwise20Filled,
    ArrowCounterclockwise20Regular,
    ShieldPerson20Filled,
    ShieldPerson20Regular,
    PersonWrench20Filled,
    PersonWrench20Regular,
    Form20Filled,
    Form20Regular
} from "@fluentui/react-icons";
import { Outlet, useLocation, Link } from "react-router-dom";
import { flexColumn, overflowHidden, wrapperContent, wrapperContentWithBackground, drawerCollapsed, drawerContent } from "../styles/GlobalStyles";
import { mobileMaxWidth } from "../util/ResponsiveDesign";

const ImprintIcon = bundleIcon(BookContacts20Filled, BookContacts20Regular);
const CookieIcon = bundleIcon(Cookies20Filled, Cookies20Regular);
const EULAIcon = bundleIcon(DocumentEdit20Filled, DocumentEdit20Regular);
const PrivacyIcon = bundleIcon(InprivateAccount20Filled, InprivateAccount20Regular);
const ReturnIcon = bundleIcon(ArrowCounterclockwise20Filled, ArrowCounterclockwise20Regular);
const DisclaimerIcon = bundleIcon(ShieldPerson20Filled, ShieldPerson20Regular);
const AcceptableUseIcon = bundleIcon(PersonWrench20Filled, PersonWrench20Regular);
const DsarIcon = bundleIcon(Form20Filled, Form20Regular);

const useStyles = makeStyles({
    drawer: {
        height: "100%",
        borderRadius: "5px"
    },
    overflowHidden: overflowHidden,
    root: {
        ...flexColumn,
        ...shorthands.margin("0px"),
        backgroundColor: tokens.colorNeutralBackground1,
        gap: "0px",
        height: "100%"
    },
    content: wrapperContent,
    contentWithBackground: wrapperContentWithBackground,
    drawerExpanded: {
        height: "100%"
    },
    navItemSmall: {
        padding: "5px"
    },
    seemlessLink: {
        color: "unset",
        textDecoration: "none"
    },
    drawerCollapsed,
    drawerContent
});

export type SelectedPage = {
    newSelectedCategory: string | undefined;
    newSelectedValue: string;
};

export const LegalDrawer: React.FC = () => {
    const styles = useStyles();
    const location = useLocation();

    const [isOpen, setIsOpen] = React.useState(() => !window.matchMedia(`(max-width: ${mobileMaxWidth}px)`).matches);
 
    const currentLocation = location?.pathname;
    const [selectedValue, setSelectedValue] = React.useState<string>(currentLocation);

    React.useEffect(() => {
        setSelectedValue(location.pathname);
    }, [location.pathname]);
    
    const hamburgerWithToolTip = React.useMemo(() => {
        return (
          <Tooltip content="Navigation" relationship="label">
            <Hamburger title={currentLocation} onClick={() => setIsOpen(!isOpen)} />
          </Tooltip>
        );
    }, [currentLocation, isOpen]);

    const navItems = React.useMemo(() => {
        return (
            <>
                <Tooltip content="Imprint" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/imprint">
                        <NavItem style={{ padding: "5px" }} icon={<ImprintIcon />} value="/legal/imprint">
                            { isOpen ? "Imprint" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Terms and Conditions" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/termsandconditions">
                        <NavItem style={{ padding: "5px" }} icon={<EULAIcon />} value="/legal/termsandconditions">
                            { isOpen ? "Terms and Conditions" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Acceptable Use Policy" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/acceptableusepolicy">
                        <NavItem style={{ padding: "5px" }} icon={<AcceptableUseIcon />} value="/legal/acceptableusepolicy">
                            { isOpen ? "Acceptable Use Policy" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Privacy Policy" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/privacypolicy">
                        <NavItem style={{ padding: "5px" }} icon={<PrivacyIcon />} value="/legal/privacypolicy">
                            { isOpen ? "Privacy Policy" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Cookie Policy" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/cookiepolicy">
                        <NavItem style={{ padding: "5px" }} icon={<CookieIcon />} value="/legal/cookiepolicy">
                            { isOpen ? "Cookie Policy" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Return Policy" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/returnpolicy">
                        <NavItem style={{ padding: "5px" }} icon={<ReturnIcon />} value="/legal/returnpolicy">
                            { isOpen ? "Return Policy" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Disclaimer" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/disclaimer">
                        <NavItem style={{ padding: "5px" }} icon={<DisclaimerIcon />} value="/legal/disclaimer">
                            { isOpen ? "Disclaimer" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="DSAR Form" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/legal/dsar">
                        <NavItem style={{ padding: "5px" }} icon={<DsarIcon />} value="/legal/dsar">
                            { isOpen ? "DSAR Form" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>
            </>
        );
    }, [isOpen, styles.seemlessLink]);

    return (
        <div id="drawerRoot" className={styles.content}>
            <div id="drawerWrapper" className={styles.drawerExpanded}>
                <NavDrawer
                    selectedValue={selectedValue}
                    open={isOpen}
                    type="inline"
                    multiple={false}
                    className={styles.drawer}
                >
                    <NavDrawerHeader>{hamburgerWithToolTip}</NavDrawerHeader>

                    <NavDrawerBody>
                        <AppItem icon={<PersonCircle32Regular />}>
                            Ianus Legal
                        </AppItem>

                        { navItems }
                    </NavDrawerBody>
                </NavDrawer>
            </div>

            {!isOpen && 
                <div className={styles.drawerCollapsed}>
                    { hamburgerWithToolTip }

                    { navItems }
                </div>
            }
            <div id="drawerContent" className={styles.drawerContent}>
                <div id="drawerOutlet" className={styles.contentWithBackground}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};